import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  BackToDefaultSelectionRequestSchema,
  BackToDefaultSelectionResponseSchema,
  FetchAdGroupResponseItemSchema,
  FetchAdGroupsOldRequestSchema,
  FetchAdGroupsOldResponseSchema,
  FetchAdGroupsRequestSchema,
  FetchAdGroupsResponseSchema,
  FetchAdResponseItemSchema,
  FetchAdsRequestSchema,
  FetchAdsResponseSchema,
  FetchImportCampaignsOldRequestSchema,
  FetchImportCampaignsRequestSchema,
  FetchImportCampaignsResponseSchema,
  FetchLoadMoreRequestSchema,
  FetchLoadMoreResponseSchema,
  FetchOmniStatusesRequestSchema,
  FetchOmniStatusesResponseSchema,
  FetchSummaryOldRequestSchema,
  FetchSummaryOldResponseSchema,
  ImportCampaignSchema,
  ImportSessionStartRequestSchema,
  ImportSessionStartResponseSchema,
  LaunchImportOldQuerySchema,
  LaunchImportOldRequestSchema,
  LaunchImportRequestSchema,
  LaunchImportResponseSchema,
  PlatformConfigurationQuerySchema,
  PlatformConfigurationResponseSchema,
  SelectAdGroupSchema,
  SelectAdSchema,
  SelectItemsRequestSchema,
  SelectItemsResponseSchema,
  TileSettingsListQuerySchema,
  TileSettingsResponseSchema,
  ValidateAdGroupsRequestSchema,
  ValidateAdGroupsResponseSchema,
  ValidateCampaignsRequestSchema,
  ValidateCampaignsResponseSchema,
} from '../shared/schemas/ImportSchema.js';

export const ImportSessionStart = defaultClient.mutation({
  url: 'omni-connector-service/import-revamp/session/start' as const,
  method: 'POST',
  requestSchema: ImportSessionStartRequestSchema,
  responseSchema: ImportSessionStartResponseSchema,
  querySchema: ImportSessionStartRequestSchema,
  processResponse(data) {
    return data;
  },
});

export const getPlatformConfiguration = defaultClient.query({
  url: 'omni-connector-service/platform-configuration/' as const,
  method: 'GET',
  responseSchema: PlatformConfigurationResponseSchema,
  querySchema: PlatformConfigurationQuerySchema,
  processResponse(data) {
    return data;
  },
});

export const getTileSettingsList = defaultClient.query({
  url: 'omni-connector-service/tile-settings/list' as const,
  method: 'GET',
  responseSchema: TileSettingsResponseSchema,
  querySchema: TileSettingsListQuerySchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data.data.items[0] || '';
  },
});

export const FetchImportCampaign = defaultClient.mutation({
  url: 'omni-connector-service/import-revamp/campaigns/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchImportCampaignsRequestSchema,
  responseSchema: FetchImportCampaignsResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const FetchImportCampaignNew = defaultClient.query({
  url: 'omni-connector-service/import-revamp/campaigns/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchImportCampaignsRequestSchema,
  responseSchema: FetchImportCampaignsResponseSchema,
  querySchema: FetchImportCampaignsRequestSchema,
});

export type FetchImportCampaignsRequestSchemaType = z.infer<
  typeof FetchImportCampaignsRequestSchema
>;

export const FetchImportCampaignOld = defaultClient.mutation({
  url: 'omni-connector-service/import/campaign/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchImportCampaignsOldRequestSchema,
  responseSchema: FetchImportCampaignsResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const SelectImportItem = defaultClient.mutation({
  url: 'omni-connector-service/import-revamp/select-entities' as const,
  method: 'POST',
  requestSchema: SelectItemsRequestSchema,
  responseSchema: SelectItemsResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export type SelectItemsRequestSchemaType = z.infer<
  typeof SelectItemsRequestSchema
>;

export type SelectItemsResponseSchemaType = z.infer<
  typeof SelectItemsResponseSchema
>;

export type SelectAdGroupSchemaType = z.infer<typeof SelectAdGroupSchema>;
export type SelectAdSchemaType = z.infer<typeof SelectAdSchema>;

export const FetchLoadMore = defaultApi.declareEndpoint({
  url: 'omni-connector-service/import-revamp/session/load' as const,
  method: 'POST',
  requestSchema: FetchLoadMoreRequestSchema,
  responseSchema: FetchLoadMoreResponseSchema,
});

export const getLoadMore = defaultClient.query({
  url: 'omni-connector-service/import-revamp/session/load' as const,
  method: 'POST',
  requestSchema: FetchLoadMoreRequestSchema,
  responseSchema: FetchLoadMoreResponseSchema,
  querySchema: FetchLoadMoreRequestSchema,
});

export type FetchLoadMoreRequestSchemaType = z.infer<
  typeof FetchLoadMoreRequestSchema
>;

export const backToDefaultSelection = defaultClient.mutation({
  url: 'omni-connector-service/import-revamp/back-to-default-selection' as const,
  method: 'POST',
  requestSchema: BackToDefaultSelectionRequestSchema,
  responseSchema: BackToDefaultSelectionResponseSchema,
  processResponse(data) {
    return data;
  },
});

export type BackToDefaultSelectionRequestSchemaType = z.infer<
  typeof BackToDefaultSelectionRequestSchema
>;

export const getOmniStatuses = defaultClient.query({
  url: 'omni-connector-service/import-revamp/supported-statuses' as const,
  method: 'GET',
  responseSchema: FetchOmniStatusesResponseSchema,
  querySchema: FetchOmniStatusesRequestSchema,
  processResponse(data) {
    return data?.data;
  },
});

export const FetchImportAdGroups = defaultApi.declareEndpoint({
  url: 'omni-connector-service/import-revamp/ad_group/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchAdGroupsRequestSchema,
  responseSchema: FetchAdGroupsResponseSchema,
});

export const getImportAdGroups = defaultClient.query({
  url: 'omni-connector-service/import-revamp/ad_group/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchAdGroupsRequestSchema,
  responseSchema: FetchAdGroupsResponseSchema,
  querySchema: FetchAdGroupsRequestSchema,
});

export type FetchedAdGroupItemType = z.infer<
  typeof FetchAdGroupResponseItemSchema
>;

export type FetchedAdGroupRequestType = z.infer<
  typeof FetchAdGroupsRequestSchema
>;

export type FetchedAdItemType = z.infer<typeof FetchAdResponseItemSchema>;

export const FetchImportAds = defaultApi.declareEndpoint({
  url: 'omni-connector-service/import-revamp/ad/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchAdsRequestSchema,
  responseSchema: FetchAdsResponseSchema,
});

export const getImportAds = defaultClient.query({
  url: 'omni-connector-service/import-revamp/ad/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchAdsRequestSchema,
  responseSchema: FetchAdsResponseSchema,
  querySchema: FetchAdsRequestSchema,
});

export type FetchAdsRequestSchemaType = z.infer<typeof FetchAdsRequestSchema>;

export const LaunchImport = defaultClient.mutation({
  url: 'omni-connector-service/import-revamp/session/launch' as const,
  method: 'POST',
  requestSchema: LaunchImportRequestSchema,
  responseSchema: LaunchImportResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const ValidateCampaigns = defaultClient.mutation({
  url: 'omni-connector-service/import/campaign/validate-import-list' as const,
  method: 'POST',
  requestSchema: ValidateCampaignsRequestSchema,
  responseSchema: ValidateCampaignsResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const ValidateAdGroups = defaultClient.mutation({
  url: 'omni-connector-service/import/ad_group/validate-import-list' as const,
  method: 'POST',
  requestSchema: ValidateAdGroupsRequestSchema,
  responseSchema: ValidateAdGroupsResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const FetchImportAdGroupsOld = defaultClient.mutation({
  url: 'omni-connector-service/import/ad_group/fetch-import-list' as const,
  method: 'POST',
  requestSchema: FetchAdGroupsOldRequestSchema,
  responseSchema: FetchAdGroupsOldResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const FetchSummaryOld = defaultClient.mutation({
  url: 'omni-connector-service/import/fetch-import-summary' as const,
  method: 'POST',
  requestSchema: FetchSummaryOldRequestSchema,
  responseSchema: FetchSummaryOldResponseSchema,
  processResponse(data) {
    return data.data;
  },
});

export const LaunchImportOld = defaultClient.mutation({
  url: 'omni-connector-service/import/launch' as const,
  method: 'POST',
  requestSchema: LaunchImportOldRequestSchema,
  responseSchema: LaunchImportResponseSchema,
  querySchema: LaunchImportOldQuerySchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data.data;
  },
});

export type ImportCampaignType = z.infer<typeof ImportCampaignSchema>;
